import React from 'react';
import Label from 'components/form/Label/Label';
import { Button } from 'components/form/Button';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import TextFromConfig from 'components/TextFromConfig';
import { get } from 'helpers/objectHelpers';
import ApprovalOddsTag from '../fragments/ApprovalOddsTag/index';
import SectionContainer from 'components/Containers/SectionContainer/index';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Image } from 'components/Image/Image';
import classnames from 'classnames';
import { showLoanTerms } from 'helpers/offer.helpers';
import PlBadgeTags from '../fragments/PlBadgeTags/PlBadgeTags';
import './StandardPersonalLoanOffer.scss';
import { PERSONAL_LOAN_VERTICAL } from 'types/marketplaceTiles.constants';
import ReviewsRating from 'components/Offers/fragments/ReviewsRating';
import { formatUSD } from 'helpers/money.helper';

function ApprovalOdds({ approvalOdds, datatestid, config }) {
  return (
    <div className="approvalOddsContainer">
      <>
        <CSTooltip
          vertical={PERSONAL_LOAN_VERTICAL}
          tooltipName="Loans Approval Odds"
          title={
            <span
              dangerouslySetInnerHTML={{
                __html: get(config, TextFromConfig.paths.DISCLAIMER_AO, ''),
              }}
            ></span>
          }
          isInline={true}
          overlayClassName="approvalOddsCSTooltip"
          className="margL0 margT0"
          datatestid={datatestid ? `${datatestid}-tooltip` : null}
        >
          <div className="approvalOddsTooltip fontGray300">
            <Label type="caption" color="fontGray300">
              Approval Odds
            </Label>
            <div className="margR4" />
            <QuestionCircleOutlined style={{ display: 'inline-block' }} />
          </div>
        </CSTooltip>
        <div className="margB4" />
        <ApprovalOddsTag approvalOdds={approvalOdds} />
      </>
    </div>
  );
}

function ApplyNowButton({
  datatestid,
  clickApplyHandler,
  clickPrequalHandler,
  name,
  prequal,
  config,
}) {
  return (
    <>
      <Button
        className="prequalButton"
        mrphType="standard"
        data-testid={datatestid ? `${datatestid}-prequal-label` : null}
        onClick={clickPrequalHandler}
        hidden={!prequal}
      >
        <Label
          datatestid={`${datatestid}-prequalButtonText`}
          type="Subtitle-2"
          color="fontBlue100"
        >
          Get Pre-Qualified
        </Label>
      </Button>
      <Button
        className="applyNowButton"
        mrphType="primary"
        data-testid={datatestid ? `${datatestid}-apply-label` : null}
        onClick={clickApplyHandler}
      >
        <FlexContainer className="justify-center align-items-center">
          <Image imgClass={'standardLockIcon'} />
          <Label
            datatestid={`${datatestid}-applyNowButtonText`}
            type="Subtitle-2"
            color="primary_cta_text_light"
            tabIndex="-1"
          >
            Apply for Offer
          </Label>
        </FlexContainer>
      </Button>

      <div
        className="thirdPartyApplyNote"
        data-testid={datatestid ? `${datatestid}-value` : null}
      >
        on {name}'s {config?.secureWebsiteDisclaimer}
      </div>
    </>
  );
}

export const Term = ({
  datatestid,
  label,
  value,
  hidePadBot,
  forceRowView,
}) => {
  const termClass = classnames(
    'flex-container m-flex-row justify-between tablet-align-items-center',
    {
      'flex-row': forceRowView,
      'flex-column': !forceRowView,
      'align-items-center': forceRowView,
    },
  );
  const valueTypeClass = classnames('H5 mSubtitle-1', {
    'Subtitle-1': forceRowView,
  });
  const termBotClass = { desktopHide: !forceRowView };
  return (
    <FlexContainer className="flex-column flex-1 m-pad4">
      <FlexContainer datatestid={`${datatestid}-term`} className={termClass}>
        <Label
          datatestid={`${datatestid}-${label}-label`}
          type="caption"
          color="fontGray400"
        >
          {label}
        </Label>
        <div className="padB4" />
        <Label
          datatestid={`${datatestid}-${label}-value`}
          type={valueTypeClass}
        >
          {value}
        </Label>
      </FlexContainer>
      {!hidePadBot && <div className={termBotClass} />}
    </FlexContainer>
  );
};

function StandardPersonalLoanOfferDesktop({
  offerMetadata,
  offerImageLinkUrlProxy,
  datatestid,
  clickDetailsHandler,
  clickReviewHandler,
  clickPrequalHandler,
  clickApplyHandler,
  seenPixel,
  extraAttributes,
  config,
  ...props
}) {
  const {
    name,
    termMonths,
    displayTerm,
    apr,
    monthlyPayment,
    loanAmount,
    totalPayout,
    tcUrl,
    imageLink,
    approvalOdds = null,
    isPrequalOffer,
  } = offerMetadata;
  const hidePrequal = extraAttributes ? extraAttributes.hidePrequal : false;
  const prequal =
    !hidePrequal &&
    offerMetadata.preApprovalInfo &&
    offerMetadata.preApprovalInfo.enabled;

  const isOcfCarousel = extraAttributes ? extraAttributes.isOcfCarousel : false;
  const showDesktop = !isOcfCarousel ? 'desktop' : 'poof';

  return (
    <div
      data-testid={datatestid}
      className={`LoanOfferModule ${showDesktop} grayShadow margB16`}
    >
      {seenPixel}
      <PlBadgeTags offerMetadata={offerMetadata} datatestid={'plbadges'} />
      <FlexContainer className="loanInfo justify-between">
        <div className="loanImageContainer">
          <img
            data-testid={datatestid ? `${datatestid}-loanImage` : null}
            alt="loanImage"
            onClick={clickDetailsHandler}
            src={offerImageLinkUrlProxy}
            className="loanImage"
          ></img>
        </div>
        {config?.showCreditCardReview && (
          <ReviewsRating
            offerMetadata={offerMetadata}
            containerCustomClass={'cardRatingContainer pointer'}
            labelCustomClass={'fontGray300 bold text-14 margt2 m-text-left'}
            badgeCustomClass={'pointer margt6'}
            onClick={clickReviewHandler}
            vertical={PERSONAL_LOAN_VERTICAL}
          />
        )}
        {isPrequalOffer && approvalOdds && (
          <ApprovalOdds
            approvalOdds={approvalOdds}
            datatestid={datatestid ? `${datatestid}-approvalOdds` : null}
            config={config}
          />
        )}

        <div className="ApplyAreaContainer">
          <ApplyNowButton
            prequal={prequal}
            datatestid={datatestid ? `${datatestid}-cardApplyButton` : null}
            clickApplyHandler={clickApplyHandler}
            clickPrequalHandler={clickPrequalHandler}
            name={name}
            config={config}
          />
        </div>
      </FlexContainer>
      <div className="margB16" />
      <div className="grayBorder" />
      <div className="margB16" />
      <LoanTermsSection
        apr={apr}
        termMonths={termMonths}
        displayTerm={displayTerm}
        loanAmount={loanAmount}
        totalPayout={totalPayout}
        monthlyPayment={monthlyPayment}
        isOcfCarousel={isOcfCarousel}
        totalCost={offerMetadata.totalCost}
        totalCostDisplay={offerMetadata.totalCostDisplay}
      />
      <Label
        type="Subtitle-3"
        className="personalLoansDetailsLink secondary_text_light pointer"
        color="fontBlue100"
        bold
        datatestid={datatestid ? `${datatestid}-moreDetails` : null}
        onClick={e => {
          clickDetailsHandler(e);
        }}
      >
        View more details
      </Label>
    </div>
  );
}

const ShowInteresFees = ({ totalCost, totalCostDisplay, showForOCF }) => {
  if (totalCost || totalCostDisplay) {
    let totalAmount;

    if (totalCostDisplay) {
      totalAmount = totalCostDisplay;
    } else {
      totalAmount = formatUSD(Math.round(totalCost));
    }
    if (totalAmount !== 'varies') {
      return (
        <>
          <Term
            hidePadBot
            label="Interest & Fees Est.*"
            value={totalAmount}
            datatestid="interestFees"
            forceRowView={showForOCF}
          />
        </>
      );
    } else {
      return '';
    }
  } else {
    return '';
  }
};

export const SortedLoanTerms = ({
  apr,
  termMonths,
  displayTerm,
  loanAmount,
  monthlyPayment,
  showForOCF,
  totalPayout,
  totalCost,
  totalCostDisplay,
}) => {
  const sortedLoanTermsClass = classnames('flex-tablet-col', {
    'flex-column': showForOCF,
  });

  const termValue = displayTerm ?? `${termMonths} mo.`;

  return (
    <FlexContainer className={sortedLoanTermsClass}>
      {loanAmount && (
        <Term
          hidePadBot
          label="Loan Amount"
          value={formatUSD(loanAmount)}
          datatestid="loanAmount"
          forceRowView={showForOCF}
        />
      )}

      {monthlyPayment && (
        <Term
          label="Monthly Payment"
          value={`${formatUSD(Math.round(monthlyPayment))}`}
          datatestid="monthlyPayment"
          forceRowView={showForOCF}
        />
      )}
      <Term
        label="Term"
        value={termValue}
        datatestid="terms"
        forceRowView={showForOCF}
      />
      <ShowInteresFees
        totalCost={totalCost}
        totalCostDisplay={totalCostDisplay}
        showForOCF={showForOCF}
      />
      <Term
        label="APR"
        value={`${parseFloat(apr).toFixed(2)}%`}
        datatestid="apr"
        forceRowView={showForOCF}
      />

      {totalPayout && !loanAmount && (
        <Term
          hidePadBot
          label="Total Cost"
          value={formatUSD(totalPayout)}
          datatestid="totalCost"
          forceRowView={showForOCF}
        />
      )}
    </FlexContainer>
  );
};

export const LoanTermsSection = ({
  apr,
  termMonths,
  displayTerm,
  loanAmount,
  totalPayout,
  monthlyPayment,
  isPrequalOffer,
  isOcfCarousel,
  showForOCF,

  totalCost,
  totalCostDisplay,
}) => {
  let showTermsSection = showLoanTerms(apr, termMonths);
  return (
    <FlexContainer className="flex-column">
      {showTermsSection && (
        <>
          <SortedLoanTerms
            apr={apr}
            termMonths={termMonths}
            displayTerm={displayTerm}
            loanAmount={loanAmount}
            monthlyPayment={monthlyPayment}
            isPrequalOffer={isPrequalOffer}
            isOcfCarousel={isOcfCarousel}
            totalPayout={totalPayout}
            showForOCF={showForOCF}
            totalCost={totalCost}
            totalCostDisplay={totalCostDisplay}
          />
          <div className="padB24 mPad28" />
        </>
      )}
    </FlexContainer>
  );
};

function StandardPersonalLoanOfferMobile({
  offerMetadata,
  offerImageLinkUrlProxy,
  datatestid,
  clickDetailsHandler,
  clickReviewHandler,
  clickPrequalHandler,
  clickApplyHandler,
  seenPixel,
  extraAttributes,
  config,
  ...props
}) {
  const {
    name,
    termMonths,
    displayTerm,
    apr,
    monthlyPayment,
    loanAmount,
    totalPayout,
    tcUrl,
    approvalOdds,
  } = offerMetadata;
  const hidePrequal = extraAttributes ? extraAttributes.hidePrequal : false;
  const prequal =
    !hidePrequal &&
    offerMetadata.preApprovalInfo &&
    offerMetadata.preApprovalInfo.enabled;

  const isOcfCarousel = extraAttributes ? extraAttributes.isOcfCarousel : false;
  const showForOCF = isOcfCarousel ? 'ocfCarousel marg8' : 'mobile';
  const isPrequalOffer = extraAttributes
    ? extraAttributes.isPrequalOffer
    : false;

  return (
    <SectionContainer
      className={`LoanOfferModule ${showForOCF} grayShadow margB16`}
    >
      <div className="loanInfo">
        <PlBadgeTags offerMetadata={offerMetadata} datatestid={'plbadges'} />
        <div className="loanImageContainer">
          <img
            data-testid={datatestid ? `${datatestid}-loanImage` : null}
            alt="loanImage"
            onClick={clickDetailsHandler}
            src={offerImageLinkUrlProxy}
            className="loanImage"
          ></img>
        </div>
      </div>
      <div className="margB8" />
      {!isOcfCarousel && <>{seenPixel}</>}
      <FlexContainer className="justify-between align-items-center margB24">
        {config?.showCreditCardReview && (
          <ReviewsRating
            offerMetadata={offerMetadata}
            containerCustomClass={'cardRatingContainer pointer'}
            labelCustomClass={'fontGray300 bold text-14 margt2 m-text-left'}
            badgeCustomClass={'pointer margt6'}
            onClick={clickReviewHandler}
            vertical={PERSONAL_LOAN_VERTICAL}
          />
        )}
        {isPrequalOffer && approvalOdds && (
          <ApprovalOdds
            approvalOdds={approvalOdds}
            datatestid={datatestid ? `${datatestid}-approvalOdds` : null}
            config={config}
          />
        )}
      </FlexContainer>

      <LoanTermsSection
        apr={apr}
        termMonths={termMonths}
        displayTerm={displayTerm}
        loanAmount={loanAmount}
        monthlyPayment={monthlyPayment}
        totalPayout={totalPayout}
        isPrequalOffer={isPrequalOffer}
        showForOCF={showForOCF}
        totalCost={offerMetadata.totalCost}
        totalCostDisplay={offerMetadata.totalCostDisplay}
      />

      <div className="ApplyAreaContainer">
        <ApplyNowButton
          prequal={prequal}
          datatestid={datatestid ? `${datatestid}-cardApplyButton` : null}
          clickApplyHandler={clickApplyHandler}
          clickPrequalHandler={clickPrequalHandler}
          name={name}
          config={config}
        />
      </div>
      <div className="margB16" />
      <Label
        type="caption"
        color="fontBlue100"
        className="text-center"
        bold
        datatestid={datatestid ? `${datatestid}-moreDetails` : null}
      >
        <a
          className="personalLoansDetailsLink"
          href={tcUrl}
          onClick={clickDetailsHandler}
          data-testid="plDetailsLink"
        >
          View more details
        </a>
      </Label>
    </SectionContainer>
  );
}

const StandardPersonalLoanOffer = props => {
  return (
    <>
      <StandardPersonalLoanOfferDesktop {...props} />
      <StandardPersonalLoanOfferMobile {...props} />
    </>
  );
};

export {
  StandardPersonalLoanOffer,
  StandardPersonalLoanOfferDesktop,
  StandardPersonalLoanOfferMobile,
};
